import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, ShoppingCart, Receipt, PackageCheck, Undo2, Trash2, BellRing,
  TrendingUp, Pencil, X, AlertTriangle, Check, Percent,
} from "lucide-react";
import { AttachmentPicker, AttachmentGrid } from "@/components/AttachmentPicker";
import { SiteSelect } from "@/components/SiteSelect";

const KINDS = [
  { key: "control_ticket", label: "Control de tickets", Ic: Receipt, color: "#0891b2" },
  { key: "recupero", label: "Recupero", Ic: PackageCheck, color: "#16a34a" },
  { key: "devolucion", label: "Devolución", Ic: Undo2, color: "#2563eb" },
  { key: "decomiso", label: "Decomiso", Ic: Trash2, color: "#BF3437" },
  { key: "alarmado", label: "Alarmado", Ic: BellRing, color: "#d97706" },
  { key: "perfil", label: "Perfil de producto", Ic: TrendingUp, color: "#7c3aed" },
  { key: "balance", label: "Balance", Ic: Percent, color: "#0d9488" },
];
const kindMeta = (k) => KINDS.find((k2) => k2.key === k) || KINDS[0];
const money = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
const pct = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  date: todayStr(), ticket_count: "", product: "", quantity: "", amount: "", quantity_current: "", percentage: "", notes: "", attachments: [],
};

export default function VigiladorOpsTasks() {
  const { user } = useAuth();
  const [allClients, setAllClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [activeKind, setActiveKind] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [pending, setPending] = useState([]);
  const [followupId, setFollowupId] = useState(null);
  const [followupForm, setFollowupForm] = useState({ quantity_current: "", sold: "", missing: "", notes: "", attachments: [] });
  const [followupBusy, setFollowupBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clients");
        const enabled = (data || []).filter((c) => c.ops_tasks_enabled);
        setAllClients(enabled);
        if (enabled.length === 1) setClientId(enabled[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const loadPending = async () => {
    try { const { data } = await api.get("/ops-tasks/pending"); setPending(data || []); }
    catch { /* silencioso */ }
  };
  useEffect(() => { loadPending(); }, []);

  const loadList = async () => {
    if (!clientId) { setList([]); return; }
    try { const { data } = await api.get("/ops-tasks", { params: { client_id: clientId } }); setList(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    loadList();
    setSiteId("");
    if (!clientId) return;
    (async () => {
      try {
        const { data } = await api.get("/ops-tasks/my-site", { params: { client_id: clientId } });
        if (data?.site_id) setSiteId(data.site_id);
      } catch { /* silencioso: la sede queda para elegir a mano */ }
    })();
  }, [clientId]);

  const openNew = (kind) => { setActiveKind(kind); setEditingId(null); setForm(emptyForm); };
  const closeForm = () => { setActiveKind(null); setEditingId(null); setForm(emptyForm); };

  const startEdit = (r) => {
    setActiveKind(r.kind);
    setEditingId(r.id);
    setForm({
      date: r.date || todayStr(), ticket_count: r.ticket_count ?? "", product: r.product || "",
      quantity: r.quantity ?? "", amount: r.amount ?? "", quantity_current: r.quantity_current ?? "",
      percentage: r.percentage ?? "",
      notes: r.notes || "", attachments: r.attachments || [],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Elegí el objetivo");
    if (activeKind === "balance" && !siteId) return toast.error("Elegí la sucursal (sede) para el balance");
    setBusy(true);
    try {
      const payload = {
        date: form.date || todayStr(), notes: form.notes || null, attachments: form.attachments,
        ticket_count: form.ticket_count === "" ? null : Number(form.ticket_count),
        product: form.product || null,
        quantity: form.quantity === "" ? null : Number(form.quantity),
        amount: form.amount === "" ? null : Number(form.amount),
        quantity_current: form.quantity_current === "" ? null : Number(form.quantity_current),
        percentage: form.percentage === "" ? null : Number(form.percentage),
      };
      if (editingId) {
        await api.patch(`/ops-tasks/${editingId}`, payload);
        toast.success("Registro actualizado");
      } else {
        await api.post("/ops-tasks", { ...payload, client_id: clientId, site_id: siteId || null, kind: activeKind });
        toast.success("Tarea registrada");
      }
      closeForm();
      loadList();
      loadPending();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const removeTask = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/ops-tasks/${id}`); toast.success("Eliminado"); loadList(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openFollowup = (r) => {
    setFollowupId(r.id);
    const initial = r.quantity_current || 0;
    setFollowupForm({ quantity_current: "", sold: "", missing: "", notes: "", attachments: [], _initial: initial });
  };

  const missingPreview = () => {
    const sold = Number(followupForm.sold || 0);
    const cur = Number(followupForm.quantity_current || 0);
    if (followupForm.quantity_current === "") return "";
    return Math.round(((followupForm._initial || 0) - sold - cur) * 100) / 100;
  };

  const submitFollowup = async (e) => {
    e.preventDefault();
    if (followupForm.quantity_current === "") return toast.error("Indicá la cantidad actual");
    setFollowupBusy(true);
    try {
      await api.post(`/ops-tasks/${followupId}/followup`, {
        quantity_current: Number(followupForm.quantity_current),
        sold: followupForm.sold === "" ? 0 : Number(followupForm.sold),
        missing: followupForm.missing === "" ? null : Number(followupForm.missing),
        notes: followupForm.notes || null,
        attachments: followupForm.attachments,
      });
      toast.success("Seguimiento completado");
      setFollowupId(null);
      loadPending();
      loadList();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setFollowupBusy(false); }
  };

  const backLink = user?.role === "admin" ? "/admin" : user?.role === "supervisor" ? "/supervisor" : "/vigilador";

  return (
    <div style={{ padding: "20px 16px 60px" }} data-testid="ops-tasks-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 14, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14} /> Volver</Link>
      <h1 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}><ShoppingCart size={20} style={{ display: "inline", marginRight: 8, color: "#BF3437" }} />Tareas operativas</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 16 }}>Control de tickets, recuperos, devoluciones, decomisos, alarmados y perfiles de producto.</p>

      {pending.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: "4px solid #7c3aed" }} data-testid="ops-tasks-pending-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} style={{ color: "#7c3aed" }} />
            <div style={{ fontWeight: 900, fontSize: 13, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.04em" }}>Seguimientos de perfil pendientes ({pending.length})</div>
          </div>
          {pending.map((p) => (
            <div key={p.id} style={{ padding: "8px 0", borderTop: "1px solid var(--invict-bone-3)" }} data-testid={`ops-pending-${p.id}`}>
              <div style={{ fontSize: 12 }}><b>{p.product}</b> · {p.client_name}{p.site_name ? ` · ${p.site_name}` : ""} · vence {p.followup_due_date}</div>
              <button type="button" onClick={() => openFollowup(p)} className="btn-primary" style={{ marginTop: 6, padding: "7px 12px", fontSize: 11 }} data-testid={`ops-pending-complete-${p.id}`}>Completar seguimiento</button>
              {followupId === p.id && (
                <form onSubmit={submitFollowup} style={{ marginTop: 10, padding: 10, background: "var(--invict-bone-2)", borderRadius: 3 }} data-testid={`ops-followup-form-${p.id}`}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div className="field"><label>Cantidad actual *</label><input type="number" min="0" step="0.01" required value={followupForm.quantity_current} onChange={(e) => setFollowupForm((f) => ({ ...f, quantity_current: e.target.value }))} data-testid="followup-quantity-current" /></div>
                    <div className="field"><label>Vendidos</label><input type="number" min="0" step="0.01" value={followupForm.sold} onChange={(e) => setFollowupForm((f) => ({ ...f, sold: e.target.value }))} data-testid="followup-sold" /></div>
                  </div>
                  <div className="field"><label>Faltantes (calculado, editable)</label><input type="number" step="0.01" value={followupForm.missing === "" ? missingPreview() : followupForm.missing} onChange={(e) => setFollowupForm((f) => ({ ...f, missing: e.target.value }))} data-testid="followup-missing" /></div>
                  <div className="field"><label>Notas</label><input value={followupForm.notes} onChange={(e) => setFollowupForm((f) => ({ ...f, notes: e.target.value }))} data-testid="followup-notes" /></div>
                  <AttachmentPicker value={followupForm.attachments} onChange={(v) => setFollowupForm((f) => ({ ...f, attachments: v }))} accept="image_video" max={5} label="Fotos del seguimiento" />
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button type="submit" disabled={followupBusy} className="btn-primary" style={{ padding: "9px 14px", fontSize: 12 }} data-testid="followup-submit">{followupBusy ? "Guardando..." : "Guardar seguimiento"}</button>
                    <button type="button" onClick={() => setFollowupId(null)} className="btn-ghost" style={{ padding: "9px 14px", fontSize: 12 }} data-testid="followup-cancel">Cancelar</button>
                  </div>
                </form>
              )}
            </div>
          ))}
        </div>
      )}

      {allClients.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center" }} data-testid="ops-tasks-no-clients">
          <ShoppingCart size={26} style={{ color: "var(--invict-text-muted)", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, fontWeight: 800 }}>Sin objetivos con Tareas operativas habilitado</div>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>Pedile al admin que active el módulo en tu objetivo.</div>
        </div>
      ) : (
        <>
          <div className="card" style={{ padding: 12, marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="ops-tasks-filters">
            <div className="field" style={{ margin: 0, flex: "1 1 200px" }}>
              <label>Objetivo</label>
              <select value={clientId} onChange={(e) => { setClientId(e.target.value); closeForm(); }} data-testid="ops-client-select">
                <option value="">Elegí un objetivo</option>
                {allClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {clientId && <div style={{ flex: "1 1 200px" }}><SiteSelect clientId={clientId} value={siteId} onChange={setSiteId} testIdPrefix="ops" /></div>}
          </div>

          {clientId && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
                {KINDS.map(({ key, label, Ic, color }) => (
                  <button key={key} type="button" onClick={() => openNew(key)} className="card"
                    style={{ padding: 12, border: activeKind === key ? `2px solid ${color}` : undefined, cursor: "pointer", textAlign: "center", background: "white" }}
                    data-testid={`ops-kind-tile-${key}`}>
                    <Ic size={20} style={{ color, margin: "0 auto 6px" }} />
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#0A0A0A" }}>{label}</div>
                  </button>
                ))}
              </div>

              {activeKind && (
                <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="ops-task-form">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>{editingId ? "Editar" : "Nuevo"} · {kindMeta(activeKind).label}</h3>
                    <button type="button" onClick={closeForm} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--invict-text-muted)" }} data-testid="ops-form-close"><X size={16} /></button>
                  </div>

                  <div className="field"><label>Fecha</label><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="ops-date" /></div>

                  {activeKind === "control_ticket" && (
                    <div className="field"><label>Cantidad de tickets *</label><input type="number" min="0" required value={form.ticket_count} onChange={(e) => set("ticket_count", e.target.value)} data-testid="ops-ticket-count" /></div>
                  )}

                  {activeKind === "recupero" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                      <div className="field"><label>Producto *</label><input required value={form.product} onChange={(e) => set("product", e.target.value)} data-testid="ops-product" /></div>
                      <div className="field"><label>Cantidad *</label><input type="number" min="0" step="0.01" required value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="ops-quantity" /></div>
                      <div className="field"><label>Valor $ *</label><input type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="ops-amount" /></div>
                    </div>
                  )}

                  {["devolucion", "decomiso", "alarmado"].includes(activeKind) && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div className="field"><label>Producto *</label><input required value={form.product} onChange={(e) => set("product", e.target.value)} data-testid="ops-product" /></div>
                      <div className="field"><label>Cantidad *</label><input type="number" min="0" step="0.01" required value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="ops-quantity" /></div>
                    </div>
                  )}

                  {activeKind === "perfil" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div className="field"><label>Producto *</label><input required value={form.product} onChange={(e) => set("product", e.target.value)} data-testid="ops-product" /></div>
                      <div className="field"><label>Cantidad actual *</label><input type="number" min="0" step="0.01" required value={form.quantity_current} onChange={(e) => set("quantity_current", e.target.value)} data-testid="ops-quantity-current" /></div>
                      <div style={{ gridColumn: "1 / -1", fontSize: 11, color: "var(--invict-text-muted)" }}>A los 5 días se te va a pedir un seguimiento (vendidos/faltantes) de este producto.</div>
                    </div>
                  )}

                  {activeKind === "balance" && (
                    <div>
                      <div className="field"><label>Porcentaje de balance (%) *</label><input type="number" min="0" max="100" step="0.01" required value={form.percentage} onChange={(e) => set("percentage", e.target.value)} data-testid="ops-percentage" /></div>
                      {!siteId && <div style={{ fontSize: 11, color: "#BF3437", fontWeight: 700, marginBottom: 8 }}>Elegí la sucursal (sede) arriba antes de guardar el balance.</div>}
                    </div>
                  )}

                  <div className="field"><label>Observaciones</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ops-notes" /></div>

                  <AttachmentPicker value={form.attachments} onChange={(v) => set("attachments", v)} accept="image_video" max={5} label="Fotos / video" />

                  <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12, marginTop: 12 }} data-testid="ops-task-submit">
                    {busy ? "Guardando..." : editingId ? "Guardar cambios" : "Registrar"}
                  </button>
                </form>
              )}

              <h3 style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--invict-text-muted)", margin: "0 0 8px" }}>Cargado recientemente</h3>
              <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="ops-tasks-list">
                {list.length === 0 && <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }}>Sin registros todavía en este objetivo.</div>}
                {list.map((r) => {
                  const meta = kindMeta(r.kind);
                  return (
                    <div key={r.id} style={{ padding: 12, borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`ops-task-row-${r.id}`}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontSize: 12 }}>
                          <span style={{ fontWeight: 800, color: meta.color }}>{meta.label}</span> · {r.date}{r.site_name ? ` · ${r.site_name}` : ""}
                          <div style={{ color: "var(--invict-text-muted)", marginTop: 2 }}>
                            {r.kind === "control_ticket" && `${r.ticket_count} tickets`}
                            {r.kind === "recupero" && `${r.product} · ${r.quantity} u. · ${money(r.amount)}`}
                            {["devolucion", "decomiso", "alarmado"].includes(r.kind) && `${r.product} · ${r.quantity} u.`}
                            {r.kind === "perfil" && `${r.product} · actual ${r.quantity_current}${r.followup_completed ? ` · faltantes ${r.followup_missing}` : " · seguimiento pendiente"}`}
                            {r.kind === "balance" && `Balance: ${pct(r.percentage)}%`}
                          </div>
                          {r.notes && <div style={{ fontStyle: "italic", color: "var(--invict-text-muted)", marginTop: 2 }}>{r.notes}</div>}
                          {r.created_by === user?.id && (
                            <>
                              {r.attachments?.length > 0 && <AttachmentGrid items={r.attachments} />}
                            </>
                          )}
                        </div>
                        {r.created_by === user?.id && (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button type="button" onClick={() => startEdit(r)} style={{ background: "none", border: "1px solid var(--invict-bone-3)", borderRadius: 2, padding: 6, cursor: "pointer" }} data-testid={`ops-edit-${r.id}`}><Pencil size={13} /></button>
                            <button type="button" onClick={() => removeTask(r.id)} style={{ background: "none", border: "1px solid var(--invict-bone-3)", borderRadius: 2, padding: 6, cursor: "pointer", color: "#BF3437" }} data-testid={`ops-delete-${r.id}`}><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                      {r.followup_completed && (
                        <div style={{ marginTop: 6, fontSize: 11, color: "#16a34a", display: "flex", alignItems: "center", gap: 4 }}><Check size={11} /> Seguimiento completado el {r.followup_date}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
